import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import {
  getAllBlogs,
  Blog,
  BlogCategory,
  createBlog,
  updateBlog,
  deleteBlog,
  getFeaturedBlogs,
  searchBlogs,
  getRelatedBlogs,
  getAllBlogCategories,
  createBlogCategory,
  updateBlogCategory,
  deleteBlogCategory,
  incrementBlogViews,
  generateSlug,
  moveBlogUp,
  moveBlogDown,
} from "../firebase/blogService";
import { Timestamp } from "firebase/firestore";
import { blogUpdateLimiter, viewIncrementLimiter } from "../utils/rateLimiter";
import { withFirebaseRetry } from "../utils/retryUtils";

interface BlogContextType {
  blogs: Blog[];
  featuredBlogs: Blog[];
  blogCategories: BlogCategory[];
  loading: boolean;
  error: Error | null;
  refreshBlogs: () => Promise<void>;
  refreshBlogCategories: () => Promise<void>;
  addBlog: (
    blog: Omit<Blog, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editBlog: (
    id: string,
    blog: Partial<Omit<Blog, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeBlog: (id: string) => Promise<void>;
  searchBlogPosts: (
    searchTerm: string,
    filters?: {
      category?: string;
      language?: "Bengali" | "English";
      difficulty?: "Beginner" | "Intermediate" | "Advanced";
    }
  ) => Promise<Blog[]>;
  getRelatedBlogPosts: (blogId: string, limit?: number) => Promise<Blog[]>;
  incrementViews: (id: string) => Promise<void>;
  addBlogCategory: (category: Omit<BlogCategory, "id">) => Promise<string>;
  editBlogCategory: (
    id: string,
    category: Partial<Omit<BlogCategory, "id">>
  ) => Promise<void>;
  removeBlogCategory: (id: string) => Promise<void>;
  moveUp: (id: string) => Promise<void>;
  moveDown: (id: string) => Promise<void>;
  generateBlogSlug: (title: string) => string;
}

const BlogContext = createContext<BlogContextType | undefined>(undefined);

export const BlogProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [featuredBlogs, setFeaturedBlogs] = useState<Blog[]>([]);
  const [blogCategories, setBlogCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchBlogs = async (forceRefresh: boolean = false) => {
    // Check if we have cached data and it's still fresh
    const now = Date.now();
    const isCacheValid =
      blogs.length > 0 && now - lastFetchTime < CACHE_DURATION;

    if (!forceRefresh && isCacheValid) {
      console.log("Using cached blog data");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use retry mechanism for fetching blogs
      const [blogsResult, featuredBlogsData] = await withFirebaseRetry(
        async () => {
          return Promise.all([getAllBlogs(), getFeaturedBlogs()]);
        }
      );

      setBlogs(blogsResult.blogs);
      setFeaturedBlogs(featuredBlogsData);
      setLastFetchTime(now);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch blogs data")
      );
      console.error("Error fetching blogs:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogCategories = async () => {
    try {
      const categoriesData = await getAllBlogCategories();
      setBlogCategories(categoriesData);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch blog categories data")
      );
      console.error("Error fetching blog categories:", err);
    }
  };

  useEffect(() => {
    fetchBlogs();
    fetchBlogCategories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshBlogs = async () => {
    await fetchBlogs(true); // Force refresh
  };

  const refreshBlogCategories = async () => {
    await fetchBlogCategories();
  };

  const addBlog = async (
    blog: Omit<Blog, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    const id = await createBlog(blog);
    // Instead of refreshing all blogs, add the new blog to the state
    const newBlog = {
      ...blog,
      id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    setBlogs((prevBlogs) => [...prevBlogs, newBlog]);
    return id;
  };

  const editBlog = async (
    id: string,
    blog: Partial<Omit<Blog, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limiting for blog updates
    if (!blogUpdateLimiter.isAllowed(`edit-${id}`)) {
      throw new Error(
        "Too many update requests. Please wait a moment before trying again."
      );
    }

    await updateBlog(id, blog);
    // Instead of refreshing all blogs, update the specific blog in state
    setBlogs((prevBlogs) =>
      prevBlogs.map((b) =>
        b.id === id
          ? ({ ...b, ...blog, updatedAt: Timestamp.now() } as Blog)
          : b
      )
    );
  };

  const removeBlog = async (id: string): Promise<void> => {
    await deleteBlog(id);
    // Instead of refreshing all blogs, remove the specific blog from state
    setBlogs((prevBlogs) => prevBlogs.filter((b) => b.id !== id));
  };

  const searchBlogPosts = async (
    searchTerm: string,
    filters?: {
      category?: string;
      language?: "Bengali" | "English";
      difficulty?: "Beginner" | "Intermediate" | "Advanced";
    }
  ): Promise<Blog[]> => {
    return await searchBlogs(searchTerm, filters);
  };

  const getRelatedBlogPosts = async (
    blogId: string,
    limit?: number
  ): Promise<Blog[]> => {
    return await getRelatedBlogs(blogId, limit);
  };

  const incrementViews = async (id: string): Promise<void> => {
    // Rate limiting for view increments to prevent spam
    if (!viewIncrementLimiter.isAllowed(`view-${id}`)) {
      // Silently skip if rate limited (views aren't critical)
      return;
    }

    try {
      await incrementBlogViews(id);
      // Update the specific blog's view count in state instead of refreshing all
      setBlogs((prevBlogs) =>
        prevBlogs.map((b) =>
          b.id === id ? { ...b, views: (b.views || 0) + 1 } : b
        )
      );
    } catch (error) {
      // Silently handle view increment errors to not disrupt user experience
      console.warn("Failed to increment views:", error);
    }
  };

  const addBlogCategory = async (
    category: Omit<BlogCategory, "id">
  ): Promise<string> => {
    const id = await createBlogCategory(category);
    // Add the new category to state instead of refreshing all
    const newCategory = { ...category, id };
    setBlogCategories((prevCategories) => [...prevCategories, newCategory]);
    return id;
  };

  const editBlogCategory = async (
    id: string,
    category: Partial<Omit<BlogCategory, "id">>
  ): Promise<void> => {
    await updateBlogCategory(id, category);
    // Update the specific category in state instead of refreshing all
    setBlogCategories((prevCategories) =>
      prevCategories.map((c) => (c.id === id ? { ...c, ...category } : c))
    );
  };

  const removeBlogCategory = async (id: string): Promise<void> => {
    await deleteBlogCategory(id);
    // Remove the specific category from state instead of refreshing all
    setBlogCategories((prevCategories) =>
      prevCategories.filter((c) => c.id !== id)
    );
  };

  const moveUp = async (id: string): Promise<void> => {
    await moveBlogUp(id);
    // For move operations, we need to refresh to get proper ordering
    // But we could optimize this further by handling the reordering in state
    await refreshBlogs();
  };

  const moveDown = async (id: string): Promise<void> => {
    await moveBlogDown(id);
    // For move operations, we need to refresh to get proper ordering
    // But we could optimize this further by handling the reordering in state
    await refreshBlogs();
  };

  const generateBlogSlug = (title: string): string => {
    return generateSlug(title);
  };

  const value: BlogContextType = {
    blogs,
    featuredBlogs,
    blogCategories,
    loading,
    error,
    refreshBlogs,
    refreshBlogCategories,
    addBlog,
    editBlog,
    removeBlog,
    searchBlogPosts,
    getRelatedBlogPosts,
    incrementViews,
    addBlogCategory,
    editBlogCategory,
    removeBlogCategory,
    moveUp,
    moveDown,
    generateBlogSlug,
  };

  return <BlogContext.Provider value={value}>{children}</BlogContext.Provider>;
};

export const useBlogs = () => {
  const context = useContext(BlogContext);
  if (context === undefined) {
    throw new Error("useBlogs must be used within a BlogProvider");
  }
  return context;
};
